// Helper de instrumentación de encuestas (MEAS-1). Emite 3 eventos del ciclo de vida de una
// encuesta a los sinks no-React (GA4 + Clarity) de forma SÍNCRONA y fire-and-forget: si un sink
// está apagado (tracking off / no-prod) no rompe la UI. Es React-free a propósito para poder
// llamarse desde hooks de respuesta (useRespondAnnouncement) y desde componentes por igual.
//
// Eventos (KEY → GA4 snake_case derivado por toGa4EventName):
//   · survey_shown    — la encuesta se mostró (una vez por comercio+encuesta, lo gobierna el caller)
//   · survey_response — el comercio eligió una opción / avanzó un paso
//   · survey_dismiss  — la encuesta se descartó sin responder
//
// Dimensiones: announcement_uuid (id de la encuesta/feature), option_value (solo en response),
// surface (modal|banner|card), rollout_variant (si el caller lo conoce — ya viaja además como
// user_property global vía applyFeatureVersionTags) y rol (user_type del comercio, desde
// localStorage) para segmentar response_rate sin exponer commerce_id.

import { ga4Event } from './ga4';
import { clarityEvent } from './clarity';
import { toGa4EventName } from './sanitize';
import type { EventParams } from './types';

export type SurveySurface = 'modal' | 'banner' | 'card';

// KEYs estables → GA4 deriva el snake_case (survey_shown/survey_response/survey_dismiss).
export const SURVEY_EVENT_KEYS = {
  shown: 'survey_shown',
  response: 'survey_response',
  dismiss: 'survey_dismiss',
} as const;

export interface TrackSurveyBase {
  announcementUuid: string;
  surface: SurveySurface;
  // Variante de rollout resuelta (control|treatment) si el caller la conoce. Opcional: para
  // encuestas no ligadas a un experimento viaja igual como user_property global en GA4.
  rolloutVariant?: string;
}

export interface TrackSurveyResponse extends TrackSurveyBase {
  optionValue: string;
}

// Rol del usuario del comercio. AuthContext persiste `user_type` en localStorage (admin/standard/
// cashier/waiter/...). No es PII y permite segmentar response_rate sin tocar commerce_id.
const readRol = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage.getItem('user_type') ?? undefined;
  } catch {
    return undefined;
  }
};

const buildParams = (base: TrackSurveyBase, extra?: EventParams): EventParams => {
  const params: EventParams = {
    announcement_uuid: base.announcementUuid,
    surface: base.surface,
    rol: readRol(),
    ...(base.rolloutVariant ? { rollout_variant: base.rolloutVariant } : {}),
    ...extra,
  };
  return params;
};

// Espeja un evento de encuesta a GA4 (snake_case, sin PII) + Clarity (evento de sesión).
// Nunca lanza: cada sink ya está aislado en su propio try/catch.
const emit = (key: string, params: EventParams): void => {
  const name = toGa4EventName(key);
  ga4Event(name, params);
  clarityEvent(name);
};

export const trackSurveyShown = (base: TrackSurveyBase): void => {
  emit(SURVEY_EVENT_KEYS.shown, buildParams(base));
};

export const trackSurveyResponse = (data: TrackSurveyResponse): void => {
  emit(SURVEY_EVENT_KEYS.response, buildParams(data, { option_value: data.optionValue }));
};

export const trackSurveyDismiss = (base: TrackSurveyBase): void => {
  emit(SURVEY_EVENT_KEYS.dismiss, buildParams(base));
};
